<template>
  <section class="auth-credentials view-container">
    <section class="view-header">
      <h1 class="view-title">Manage Platform Connections</h1>
      <Sublinks></Sublinks>
    </section>

    <Overlay v-if="isAddOverlayOpen" :closeClick="closeOverlay">
      <div class="widget add-cred-overlay">
        <div @click="closeOverlay()">
          <DisplaySvg name="x" class="close-icon" />
        </div>
        <AddForm :closeClick="closeOverlay" :orgid="isAddOverlayOpen.orgid" :editContainer="isAddOverlayOpen" />
      </div>
    </Overlay>

    <Overlay v-if="isRemoveOverlayOpen" :closeClick="closeOverlay">
      <div class="widget add-cred-overlay">
        <div @click="closeOverlay">
          <DisplaySvg name="x" class="close-icon" />
        </div>
        <RemoveForm :closeClick="closeOverlay" :containerid="isRemoveOverlayOpen.containerid"
          :orgid="isRemoveOverlayOpen.orgid" />
      </div>
    </Overlay>

    <div class="workarea">
      <div class="widget full auth-credentials">
        <div class="table-wrapper">
          <div class="table-header cred-table-header">
            <div class="manage-account-cred-title">
              <h2>Platform Connections</h2>
              <p>
                Manage your organization's platform connections for AdTech DNA.
              </p>
            </div>
            <div class="filters" v-if="$store.state.organizationOptions">
              
            </div>
          </div>
          <div class="table-header cred-table-sub-header">
            <div class="manage-account-cred-title">
              
            </div>
            <div class="filters" v-if="!loading">
              <NInput v-model:value="searchQuery" placeholder="Search" v-if="tableData.length > 0 && !loading">
                <template #suffix>
                  <DisplaySvg name="search" class="search-icon" />
                </template>
              </NInput>
              <button class="button blue-btn" :onClick="() => {
                addContainer(false);
              }
                ">
                Add Connection
                <DisplaySvg name="plus" class="plus-icon" />
              </button>
            </div>
          </div>
        </div>
        <NDataTable v-if="
          tableData.length > 0 &&
          !error &&
          !loading
        " :columns="tableColumns" :data="filteredData" :bordered="false" :pagination="pagination"
          class="full manage-account-creds--users-table"></NDataTable>
        <div class="site-message" v-if="tableData.length == 0 && !loading">
          The {{ $store.state.organization }} organization currently has no connections at the base level.
        </div>
        <div class="site-error" v-if="error && !loading">{{ error }}</div>
        <div class="site-loading" v-if="!error && loading">
          <LoadingSpinner /> Loading...
        </div>
      </div>
    </div>
  </section>
</template>

<script>
//import router from "@/router";
import MetadataService from "@/services/MetadataService";
import EllipsisDropdown from "@/components/Shared/EllipsisDropdown.vue";
import DisplaySvg from "@/components/Shared/DisplaySvg.vue";
import Overlay from "@/components/Shared/Overlay.vue";
import Sublinks from "@/components/ManageAccount/Sublinks.vue";
import AddForm from "@/components/ManageAccount/Containers/AddForm.vue";
import RemoveForm from "@/components/ManageAccount/Containers/RemoveForm.vue";
import LoadingSpinner from "@/components/Shared/LoadingSpinner.vue";
import { NDataTable, NInput } from "naive-ui";
import { h } from "vue";

export default {
  name: "ManageAccountContainers",
  components: {
    NDataTable,
    NInput,
    DisplaySvg,
    AddForm,
    RemoveForm,
    Overlay,
    LoadingSpinner,
    Sublinks
  },
  computed: {
    filteredData() {
      return this.tableData.filter((item) =>
        Object.values(item).some((value) =>
          value !== null && value !== undefined && // Ensure value is not null or undefined
          value.toString().toLowerCase().includes(this.searchQuery.toLowerCase())
        )
      );
    },
    pagination() {
      return {
        pageSize: 10,
        disabled: this.filteredData.length <= 10 ? true : false,
      };
    },
  },
  methods: {
    addContainer() {
      // Opens the overlay in "Add" mode
      this.isAddOverlayOpen = {
        container: null, // Pass `null` to indicate adding a new container
        orgid: this.$store.state.organization,
      };
    },
    editContainer(container) {
      // Opens the overlay in "Edit" mode, passing the existing container data
      this.isAddOverlayOpen = {
        container: container || null, // Ensure a valid container object or `null`
        orgid: this.$store.state.organization,
      };
    },
    removeContainer(container) {
      this.isRemoveOverlayOpen = {
        containerid: container ? container.containerid : false,
        orgid: this.$store.state.organization,
      };
    },
    closeOverlay() {
      this.isAddOverlayOpen = false;
      this.isRemoveOverlayOpen = false;
      this.getData();
    },
    async getData() {
      if (this.getDataActive) return;

      this.getDataActive = true;
      this.loading = true;

      try {
        const fetchAllPages = async (pageToken = null, accumulatedData = []) => {
          const params = {
            orgid: this.$store.state.organization,
            filter: { deleted: false },
          };

          if (pageToken) {
            params.pageToken = pageToken;
          }

          const response = await MetadataService.call("analyticscontainers.list", params);

          if (!response || !response.result || !response.result.containers) {
            return accumulatedData;
          }

          const currentPageData = response.result.containers.map((container) => ({
            ...container,
            type: Object.keys(container.extservices || {})[0],
          }));

          const allData = [...accumulatedData, ...currentPageData];

          if (response.result.hasMore) {
            return fetchAllPages(response.result.pageToken, allData);
          }

          return allData;
        };

        const tableData = await fetchAllPages();
        this.tableData = [...tableData];
        this.setTableColumns();
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        this.getDataActive = false;
        this.loading = false;
      }
    },
    setTableColumns() {
      const editRowOnClick = (row) => {
        this.editContainer(row);
      };
      const removeRowOnClick = (row) => {
        this.removeContainer(row);
      };

      this.tableColumns = [
        {
          title: "Name",
          minWidth: 200,
          render(row) {
            return h("p", {}, row.name);
          },
        },
        {
          title: "Service",
          minWidth: 120,
          render(row) {
            return h("p", {}, row.type);
          },
        },
        {
          title: "",
          key: "action",
          minWidth: 100,
          render(row) {
            const editOnClick = () => {
              editRowOnClick(row);
            };
            const removeOnClick = () => {
              removeRowOnClick(row);
            };

            const options = [
              {
                key: `${row.containerid}-edit`,
                onClick: editOnClick,
                text: "Edit",
              },
              {
                key: `${row.containerid}-remove`,
                onClick: removeOnClick,
                text: "Remove",
                class: "red",
              },
            ];

            return h(
              EllipsisDropdown,
              {
                options: options,
              },
              {
                // Provide an empty slot function for better performance
                default: () => null,
              }
            );
          },
        },
      ];
    },
  },
  data() {
    return {
      error: false,
      loading: true,
      getDataActive: false, // only used to tell if the current getData call is running
      NODE_ENV: process.env.NODE_ENV,
      isAddOverlayOpen: false,
      isRemoveOverlayOpen: false,
      searchQuery: "",
      tableColumns: [],
      tableData: [],
      hierarchyString: "root",
      rootClientId: null,
    };
  },
  watch: {
    "$store.state.orgHierarchy": function () {
      if (
        (this.$store.state.organizationOptions &&
          this.$store.state.organizationOptions.length > 0 &&
          this.$store.state.organization &&
          this.$store.state.userData &&
          this.$store.state.orgHierarchy &&
          this.$store.state.orgHierarchy.length > 0) ||
        process.env.NODE_ENV === "demo"
      ) {
        this.getData();
      }
    },
    "$store.state.organizationOptions": function () {
      if (
        (this.$store.state.organizationOptions &&
          this.$store.state.organizationOptions.length > 0 &&
          this.$store.state.organization &&
          this.$store.state.userData &&
          this.$store.state.orgHierarchy &&
          this.$store.state.orgHierarchy.length > 0) ||
        process.env.NODE_ENV === "demo"
      ) {
        this.getData();
      }
    },
    "$store.state.userData": function () {
      if (
        (this.$store.state.organizationOptions &&
          this.$store.state.organizationOptions.length > 0 &&
          this.$store.state.organization &&
          this.$store.state.userData &&
          this.$store.state.orgHierarchy &&
          this.$store.state.orgHierarchy.length > 0) ||
        process.env.NODE_ENV === "demo"
      ) {
        this.getData();
      }
    },
    "$store.state.organization": function () {
      if (
        (this.$store.state.organizationOptions &&
          this.$store.state.organizationOptions.length > 0 &&
          this.$store.state.organization &&
          this.$store.state.userData &&
          this.$store.state.orgHierarchy &&
          this.$store.state.orgHierarchy.length > 0) ||
        process.env.NODE_ENV === "demo"
      ) {
        this.getData();
      }
    },
  },
  mounted() {
    if (this.$store.state.organization != null) this.getData();
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/_helpers.scss";
</style>
